import { useMemo } from 'react';
import type { VoidFunctionComponent } from 'react';

import Badge, { VulnBadgeColors } from '../Badge/Badge';
import Icon, { icons } from '../Icon';
import Tooltip, { TooltipContent } from '../Tooltip';

type Props = {
  score: number;
  hasTooltip?: boolean;
  showScore?: boolean;
};

type BadgeConfig = {
  label: string;
  color: VulnBadgeColors;
};

const useBadgeConfig = (score: number): BadgeConfig =>
  useMemo(() => {
    if (score < 0 || score > 100) {
      return {
        label: 'Unknown',
        color: VulnBadgeColors.Unknown,
      };
    }
    if (score >= 90) {
      return {
        label: 'Critical',
        color: VulnBadgeColors.Critical,
      };
    }
    if (score >= 70) {
      return {
        label: 'High',
        color: VulnBadgeColors.High,
      };
    }
    if (score >= 40) {
      return {
        label: 'Medium',
        color: VulnBadgeColors.Medium,
      };
    }

    return {
      label: 'Low',
      color: VulnBadgeColors.Low,
    };
  }, [score]);

/**
 * Displays a colour badge based on the custom risk score (0-100).
 *
 * Thresholds:
 *  90-100 = Critical
 *  70-89  = High
 *  40-69  = Medium
 *  0-39   = Low
 */
const CustomRiskScoreBadge: VoidFunctionComponent<Props> = ({
  score,
  hasTooltip = true,
  showScore = true,
}) => {
  const config = useBadgeConfig(score);

  return (
    <Tooltip
      showTooltip={hasTooltip}
      id={`custom-risk-score-badge-${config.label}-${score}`}
      tooltip={
        <TooltipContent>
          <div>{config.label} custom risk score: {score}</div>
        </TooltipContent>
      }
    >
      <Badge color={config.color}>
        <Icon icon={icons.asterisk} /> {config.label}
        {showScore && ` ${score}`}
      </Badge>
    </Tooltip>
  );
};

export default CustomRiskScoreBadge;
